import { useMutation, useQueryClient } from '@tanstack/react-query';

import i18n from '@/core/i18n';
import { displayToastError, displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateAfterMemberAccessChange } from '@/modules/common/utils/query-invalidation';

import { bulkInviteTeamMembers } from '../../api/member-access.api';
import { BulkInviteTeamMembersRequest } from '../../types/request/member-access-request';

export const useBulkInviteTeamMembers = () => {
  const queryClient = useQueryClient();
  const { getErrorMessage } = useApiError();

  return useMutation({
    mutationFn: (request: BulkInviteTeamMembersRequest) => bulkInviteTeamMembers(request),
    onSuccess: async data => {
      // Inviting flips members Basic→Invited; refresh the member-access list (active
      // refetch + lazy revisit for other filters) and mark team-page surfaces stale lazily.
      await invalidateAfterMemberAccessChange(queryClient);

      const succeeded = data.results.filter(r => r.success).length;
      const failed = data.results.filter(r => !r.success).length;

      if (failed === 0) {
        displayToastSuccess(
          i18n.t('authenticated.settings:pages.members.inviteSuccess', { count: succeeded })
        );
      }
      // Partial / all-failed cases: caller shows InviteFailuresDialog with per-member details
    },
    onError: (error: unknown) => {
      displayToastError(getErrorMessage(error, true));
    },
  });
};
